import { Component, DestroyRef, inject, Inject, Injector, OnInit } from '@angular/core';
import { FormGroup, Validators, FormControl, NonNullableFormBuilder } from '@angular/forms';
import { MatDialogActions, MatDialogClose, MatDialogContent, MatDialogModule, MatDialogRef, MatDialogTitle, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { DropDownItem, VerifyCollaboratorRequest } from '../../../../models/dtos';
import { IAddCollaboratorFormGroup } from '../../../../models/properties/IAddCollaboratorFormGroup';
import { CollaborationApiService } from '../../../../services/api/collaboration-api.service';
import { NotificationMessageComponent } from '../notification-message/notification-message.component';
import { CommonDataService } from '../../../../services/common-data.service';
import { UserProfileService } from '../../../../services/common/user-profile.service';
import { CONSTANTS } from '../../../../Constants/Constants';
import { CollaboratorDetails } from '../../../../models/dtos/Classes/CollaboratorDetails';
import { IAddCollaboratorDialogData } from '../../../../models/preferences/IAddCollaboratorDialogData';
import { CloseOnEscapeDirective } from '../../../../directives/close-on-escape.directive';
import { emailsMatch } from '../../../../services/collab-common-functions';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map, merge } from 'rxjs';

@Component({
    selector: 'app-add-collaborator-dialog',
    templateUrl: './add-collaborator-dialog.component.html',
    styleUrls: ['./add-collaborator-dialog.component.scss'],
    imports: [
        MatSlideToggleModule,
        TranslatePipe,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        ReactiveFormsModule,
        CommonModule,
        MatDialogTitle,
        MatDialogContent,
        MatDialogActions,
        MatDialogClose,
        MatButtonModule,
        MatDialogModule,
        CloseOnEscapeDirective
    ]
})
export class AddCollaboratorDialogComponent implements OnInit {

    availableReviewers: CollaboratorDetails[] = [];
    collaboratorRoles: DropDownItem[] = [];
    isModifyMode: boolean = false;
    propertiesCollaboratorForm!: FormGroup<IAddCollaboratorFormGroup>;
    destroyRef = inject(DestroyRef);

    constructor(
        private readonly dialogRef: MatDialogRef<AddCollaboratorDialogComponent>,
        private readonly fb: NonNullableFormBuilder,
        private readonly collaborationApiService: CollaborationApiService,
        private readonly dialog: MatDialog,
        private readonly commonDataService: CommonDataService,
        private readonly userProfileService: UserProfileService,
        private readonly translateService: TranslateService,
        @Inject(MAT_DIALOG_DATA) readonly dialogData: IAddCollaboratorDialogData
    ) {
    }

    ngOnInit(): void {
        this.initializeForm();
        this.loadAvailableReviewers();
        this.loadCollaboratorRoles();
        this.subscribeFormEmail();
    }

    get email(): FormControl<string> {
        return this.propertiesCollaboratorForm.controls.email as FormControl<string>;
    }

    get canEdit(): FormControl<boolean> {
        return this.propertiesCollaboratorForm.controls.canEdit as FormControl<boolean>;
    }

    subscribeFormEmail(): void {
        const { email, name, role, verified } = this.propertiesCollaboratorForm.controls;
        merge(
            email.valueChanges.pipe(map(() => true)),
            name.valueChanges.pipe(map(() => false)),
            role.valueChanges.pipe(map(() => false))
        ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(emailChanged => {
            const isAvailable = this.availableReviewers.some(reviewer => emailsMatch(reviewer.email, email.value));
            if (emailChanged || isAvailable) {
                verified.setValue(isAvailable && email.valid && name.valid && role.valid);
            }
        });
    }

    enableAdd(): boolean {
        return this.propertiesCollaboratorForm.valid && this.propertiesCollaboratorForm.controls.verified.value;
    }

    initializeForm(): void {
        this.propertiesCollaboratorForm = this.fb.group<IAddCollaboratorFormGroup>({
            availableCollaborator: this.fb.control<number>(CONSTANTS.NEGATIVE_ONE, Validators.required),
            role: this.fb.control<number>(CONSTANTS.NEGATIVE_ONE, [Validators.required, Validators.min(0)]),
            name: this.fb.control<string>('', Validators.required),
            email: this.fb.control<string>('', [Validators.required, Validators.email]),
            verified: this.fb.control<boolean>(false),
            canEdit: this.fb.control<boolean>(false)
        });
    }

    onAvailableReviewerChange(event: MatSelectChange): void {
        const selectedReviewer = this.availableReviewers.find(r => emailsMatch(r.email, event.value));
        if (selectedReviewer) {
            this.propertiesCollaboratorForm.patchValue({
                name: selectedReviewer.name,
                email: selectedReviewer.email,
                role: selectedReviewer.roleId,
                canEdit: selectedReviewer.canEdit,
                verified: true
            });
        }
    }

    //TODO: Implement role change logic when roles are defined
    onRoleChange(event: MatSelectChange): void {
        // Handle role change logic here
        console.log('Role changed:', event.value);
    }

    onVerifyClick(): void {
        const email = this.email.value;
        if (!email) {
            return;
        }

        const alreadyAdded = (this.dialogData?.alreadyAddedCollaborators ?? [])
            .filter(collaborator => emailsMatch(collaborator.email, email));

        if (alreadyAdded.length) {
            const currentUser = this.userProfileService.userInfo?.mail
            let message = emailsMatch(alreadyAdded[0].email, currentUser) || emailsMatch(email, currentUser)
                ? this.translateService.instant('CANNOT_ADD_LOGGED_IN_USER_AS_COLLABORATOR')
                : this.translateService.instant('COLLABORATOR_ALREADY_IN_LIST');
            this.dialog.open(NotificationMessageComponent, {
                data: { type: 'error', title: 'Error', message: message }
            });
            this.propertiesCollaboratorForm.controls.verified.patchValue(false);
            return;
        }

        const payload: VerifyCollaboratorRequest = {
            collaboratorEmail: email,
            userId: this.userProfileService.userInfo?.gid ?? '',
            estimateId: this.commonDataService.getEstimateId(),
            isArchive: this.commonDataService.isArchivedEstimate()
        };

        this.collaborationApiService.verifyCollaborator(payload).subscribe({
            next: (result) => {
                if (result) {
                    this.propertiesCollaboratorForm.controls.verified.setValue(true);
                } else {
                    this.dialog.open(NotificationMessageComponent, {
                        data: { type: 'error', title: 'Error', message: 'Verification failed' }
                    });
                    this.propertiesCollaboratorForm.controls.verified.setValue(false);
                }
            }
        });
    }
    enableVerify(): boolean {
        return this.propertiesCollaboratorForm.controls.email.valid && !this.propertiesCollaboratorForm.controls.verified.value;
    }

    onAdd(): void {
        if (this.propertiesCollaboratorForm.valid) {
            const formValue = this.propertiesCollaboratorForm.value;
            const collaboratorData: CollaboratorDetails = {
                name: formValue.name,
                email: formValue.email,
                role: CONSTANTS.EMPTY_STRING, // Role name can be set here if needed
                roleId: Number(formValue.role),
                canEdit: this.canEdit.value,
                isPrimaryUser: false
            };

            // Close dialog and return the form data
            this.dialogRef.close(collaboratorData);
        }
    }

    private loadAvailableReviewers(): void {
        const availableCollaborators = this.commonDataService.getAllCollaboratorPreferences()?.availableCollaborators ?? [];
        this.availableReviewers = availableCollaborators.filter(collaborator =>
            !emailsMatch(collaborator.email, this.userProfileService.userInfo?.mail) &&
            !(this.dialogData?.alreadyAddedCollaborators ?? []).some(existing => emailsMatch(existing.email, collaborator.email))
        );
    }

    private loadCollaboratorRoles(): void {
        const excludedRoleIds = new Set(this.dialogData?.excludedCollaboratorRoleIds ?? []);
        this.collaboratorRoles = (this.dialogData?.collaboratorRoles ?? [])
            .filter(role => !excludedRoleIds.has(role.id));
    }

}